import { ArrowCircleDownIcon, ArrowCircleRightIcon } from "@heroicons/react/outline";
import moment from "moment";
import cx from 'classnames';
import { Fragment, useState } from "react";
import { TimesheetProofingDistrictModel } from "../../api/models/timesheetProofingDistrictModel";
import { TimesheetStatus } from "../../common/constants";
import { CheckCircleIcon, ExclamationCircleIcon } from "@heroicons/react/solid";
import { useHistory } from "react-router-dom";

type DistrictsProps = {
    districts: TimesheetProofingDistrictModel[],
}

type DistrictProps = {
    district: TimesheetProofingDistrictModel,
}

const getFormattedTimeFromMinutes = (totalMinutes: number): string => {
    const duration = moment.duration(totalMinutes, 'minutes');
    const hours = Math.floor(duration.asHours());
    const minutes = Math.floor(duration.asMinutes()) - hours * 60;
    return `${hours}h ${minutes}m`;
}

const RowStatusIcon = ({ district }: DistrictProps) => {
    if (district.issueCount === 0 && district.weeks?.every(w =>
        w.status.timesheetStatusId === TimesheetStatus.Submitted)) {
        return <p className='text-xs italic text-gray-500 uppercase'>Pending<br />Approval</p>
    }
    return (
        district.issueCount > 0
            ? <ExclamationCircleIcon
                className='w-12 h-12 text-red-500'
                aria-hidden='true'
            />
            : <CheckCircleIcon
                className='w-12 h-12 text-green-600'
                aria-hidden='true'
            />
    );
}

const TimesheetDistrictEntries = ({ districts }: DistrictsProps) => {
    const history = useHistory();

    const [expandedDistrict, setExpandedDistrict] = useState<Number>(-2);
    const [expandAll, setExpandAll] = useState<boolean>(false);

    const getIsExpandable = (district: TimesheetProofingDistrictModel, index: number): boolean =>
        ((expandedDistrict === index || expandAll) && district.weeks?.length > 0);

    /**
     * Expand/Collapse the entry for a district
     * @param index 
     */
    const toggleDistrict = (index: number) => {
        // If we have all expanded then collapse all of them
        if (expandAll) {
            setExpandAll(false);
            setExpandedDistrict(-1);
            return;
        }

        // If the week is already expanded set to -1 to collapse it
        setExpandedDistrict((expandedDistrict === index) ? -1 : index);
    }

    const navigateToTimesheetWeek = (weekId: number): void => {
        history.push(`/proofing/week/${weekId}`);
    }

    const navigateToTimesheetSummary = (dist: TimesheetProofingDistrictModel): void => {

        history.push(`/proofing/summary/${dist.districtId}`, { district: dist });
    }

    return (
        <div className='timesheet-district-entries'>
            {/* If no districts */}
            {districts?.length <= 0 &&
                <div className='text-center text-gray-500 empty-timesheet-districts-result'>
                    No districts in the selected range
                </div>
            }

            {/* Expand all link */}
            {districts?.length > 0 &&
                <div className='pb-2 pl-2 text-right'>
                    <span
                        className='cursor-pointer text-ptsblue-normal hover:underline'
                        onClick={() => { setExpandAll(!expandAll); setExpandedDistrict(-1); }}>
                        {expandAll ? "Collapse All" : "Expand All"}
                    </span>
                </div>
            }

            {/* Districts */}
            {districts?.map((district, index) => (
                <div
                    key={`districtWithErrorCount_${district.districtId}`}
                    id={`district_${district.districtId}`}
                    className='expandable-panel-header bg-gray-100 rounded-lg p-0.5 px-1.5 mb-3'
                >
                    <div onClick={() => toggleDistrict(index)}
                        className={
                            cx({
                                'flex items-center': true,
                                'cursor-pointer': district.weeks?.length > 0,
                            })
                        }
                    >
                        {district.weeks?.length > 0
                            ?
                            <div className='expandable-icon'>
                                {getIsExpandable(district, index) && (
                                    <ArrowCircleDownIcon className='w-6 mt-1 mr-1 text-gray-800' />
                                )}
                                {!getIsExpandable(district, index) && (
                                    <ArrowCircleRightIcon className='w-6 mt-1 mr-1 text-gray-800' />
                                )}
                            </div>
                            :
                            <div className='empty-icon-placeholder w-7'></div>
                        }

                        {/* Group Labels */}
                        <div className='w-1/2 h-full'>
                            <div className={
                                cx({
                                    'text-xl font-bold ml-12': true,
                                    'text-red-500': district.issueCount > 0
                                })
                            }>
                                {district.districtName}
                            </div>
                        </div>
                        {/* Totals */}
                        <div className='w-1/2 h-full'>
                            <div className='flex justify-between my-4'>
                                <div>
                                    <p className='text-sm font-bold'>Weeks</p>
                                    <p className={
                                        cx({
                                            'text-center': true,
                                            'text-red-500 font-bold': district.issueCount > 0
                                        })
                                    }>

                                        {district.approvedTimesheets} / {district.totalTimesheets}
                                    </p>
                                </div>
                                <div>
                                    <p className='text-sm font-bold'>Issues</p>
                                    <p className={
                                        cx({
                                            'text-center': true,
                                            'text-red-500 font-bold': district.issueCount > 0
                                        })
                                    }>
                                        {district.issueCount}
                                    </p>
                                </div>
                                <div>
                                    <RowStatusIcon district={district} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='expandable-panel'>
                        {/* Entries */}
                        {getIsExpandable(district, index) &&
                            <Fragment>
                                <hr style={{ backgroundColor: 'lightGray', border: '0', height: '1px', width: '90%', margin: 'auto' }}></hr>
                                <table className='mx-auto my-5 text-sm text-left' style={{ width: '90%', textAlign: 'left' }}>
                                    <thead className='font-bold text-center'>
                                        <tr>
                                            <th><a href="#" onClick={() => {
                                                navigateToTimesheetSummary(district);
                                            }}>View as List</a></th>
                                            <th>Week</th>
                                            <th>Time</th>
                                            <th>Overage<br /> Allowance</th>
                                            <th>Avg Other<br /> / Week</th>
                                            <th>Avg Contacts<br /> / 7.5h day</th>
                                            <th>Avg Indirect<br /> / Session</th>
                                            <th>Efficiency</th>
                                            <th>Flags</th>
                                            <th>Updated</th>
                                        </tr>
                                    </thead>
                                    <tbody className='text-center'>
                                        {district.weeks?.map((week, index) => (
                                            <tr key={`timesheetWeek_${index}`} className="h-16">
                                                <td className='text-left'>
                                                    <div className='font-bold'>{week.therapistName}</div>
                                                    {week.disciplineName}
                                                </td>
                                                <td>
                                                    <p onClick={() => navigateToTimesheetWeek(week.weekId)}
                                                        className="font-bold text-red-500 underline cursor-pointer hover:text-gray-500"
                                                    >{week.weekRangeDisplay}</p>
                                                </td>
                                                <td>{getFormattedTimeFromMinutes(week.totalMinutes)}</td>
                                                <td>{week.dailyOverageAllowance}</td>
                                                <td>{week.avgOtherPerWeek}</td>
                                                <td>{week.avgContactsPerProofingDay}</td>
                                                <td>{week.avgIndirectPerSession}</td>
                                                <td>{week.efficiencyActual} / {week.efficiencyTarget}</td>
                                                <td>
                                                    <p onClick={() => (week.timesheetEntryFlagsCount > 0 && navigateToTimesheetWeek(week.weekId))}
                                                        className={
                                                            cx({
                                                                'text-red-500 font-bold underline cursor-pointer hover:text-gray-500':
                                                                    week.timesheetEntryFlagsCount > 0,
                                                            })
                                                        }
                                                    >
                                                        {week.timesheetEntryFlagsCount} Issue(s)
                                                    </p>
                                                </td>
                                                <td>{moment(week.timesheetLastUpdated).format('M/D/YY')}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </Fragment>
                        }
                    </div>
                </div>
            ))}
        </div>
    )
}

export default TimesheetDistrictEntries;
